import { useState, useRef } from "react";
import NoteEditor from "@/components/foundations/inputs/NoteEditor/NoteEditor";

type Colors = {
  bar: string;
  soft: string;
  ink: string;
};
type Props = {
  /** The saved value from the server */
  savedValue: string;
  /** Called when user explicitly taps Save */
  onSave: (value: string) => void;
  /** Called whenever the local draft changes */
  onDraftChange?: (value: string) => void;
  /** Whether a save is in progress */
  isSaving?: boolean;
  /** Called when the user discards the local draft */
  onCancel?: () => void;
  colors?: Colors;
  placeholder?: string;
};

/**
 * Standalone note editor with explicit Save-only behavior.
 * - Draft is local until Save is tapped.
 * - Back navigation can observe the draft through onDraftChange.
 * - Cancel discards the local draft.
 */
export default function TaskNoteEditor({
  savedValue,
  onSave,
  onDraftChange,
  onCancel,
  isSaving = false,
  colors,
  placeholder = "Private notes for this task…",
}: Props) {
  const [draft, setDraft] = useState(savedValue);
  const prevSavedRef = useRef(savedValue);
  if (prevSavedRef.current !== savedValue) {
    prevSavedRef.current = savedValue;
    setDraft(savedValue);
  }
  const isDirty = draft !== savedValue;
  const updateDraft = (value: string) => {
    setDraft(value);
    onDraftChange?.(value);
  };

  return (
    <NoteEditor
      value={draft}
      onChange={updateDraft}
      onSave={() => onSave(draft)}
      onCancel={onCancel}
      isSaving={isSaving}
      saveDisabled={!isDirty}
      colors={colors}
      placeholder={placeholder}
    />
  );
}
